import React from 'react';
import { Building2, FileText, CreditCard, Percent, Truck, LifeBuoy } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const NewServicesSection = () => {
  const { language } = useLanguage();
  const sl = language === 'sl';

  const services = sl ? [
    {
      icon: Building2,
      title: 'Ustanovitev podjetja',
      desc: 'Izberemo pravo pravno obliko, pripravimo vse dokumente in registriramo vaše podjetje — d.o.o., podružnico ali s.p.',
      features: ['Ustanovitev na daljavo s pooblastilom', 'Poslovni naslov, bančni račun, davčna številka', 'Urejeno v 7–14 dneh']
    },
    {
      icon: FileText,
      title: 'Računovodstvo in DDV',
      desc: 'Digitalno, brezpapirno knjigovodstvo s poročili v razumljivem jeziku — da svoje številke res razumete.',
      features: ['Mesečno poročanje in letna poročila', 'Čezmejni DDV', 'Zahtevki za vračilo DDV v EU']
    },
    {
      icon: CreditCard,
      title: 'Pridobitev delovnih dovoljenj',
      desc: 'Vodimo celoten birokratski postopek, od začetka do konca, in skrbimo za vašo skladnost kot delodajalca.',
      features: ['Enotna dovoljenja za prebivanje in delo, podaljšanja', 'Vsa komunikacija z upravnimi organi', 'Obračun plač in obrazci A1']
    },
    {
      icon: Percent,
      title: 'Davčno svetovanje',
      desc: 'Nekdo, ki vam pove, koliko stane odločitev, preden jo sprejmete.',
      features: ['Davčna optimizacija', 'Načrtovanje denarnega toka', 'Repatriacija dobička in prestrukturiranje']
    },
    {
      icon: Truck,
      title: 'Za prevoznike',
      desc: 'Prevozniki so velik del naših strank — vašo dokumentacijo poznamo do potankosti.',
      features: ['Licence za prevoz', 'Dovoljenja za voznike, plače in A1', 'Vračila DDV za gorivo in cestnine v tujini']
    },
    {
      icon: LifeBuoy,
      title: 'Sanacija podjetij',
      desc: 'Ko postane tesno, potrebujete načrt in nekoga ob sebi za mizo z upniki.',
      features: ['Finančna analiza in načrti sanacije', 'Pogajanja z upniki', 'Prisilna poravnava in prestrukturiranje']
    }
  ] : [
    {
      icon: Building2,
      title: 'Company formation',
      desc: 'We choose the right legal form, prepare all documents and register your company — d.o.o., branch or s.p.',
      features: ['Remote setup with power of attorney', 'Business address, bank account, tax number', 'Done in 7–14 days']
    },
    {
      icon: FileText,
      title: 'Accounting and VAT',
      desc: 'Digital, paperless bookkeeping with reports in plain language — so you really understand your numbers.',
      features: ['Monthly reporting and annual accounts', 'Cross-border VAT', 'EU VAT refund claims']
    },
    {
      icon: CreditCard,
      title: 'Work permits',
      desc: 'We run the entire bureaucratic process from start to finish and ensure your compliance as an employer.',
      features: ['Single residence and work permits, renewals', 'All communication with authorities', 'Payroll and A1 forms']
    },
    {
      icon: Percent,
      title: 'Tax advisory',
      desc: 'Someone who tells you what a decision costs before you make it.',
      features: ['Tax optimization', 'Cash flow planning', 'Profit repatriation and restructuring']
    },
    {
      icon: Truck,
      title: 'For hauliers',
      desc: 'Hauliers are a big part of our clients — we know your documentation inside out.',
      features: ['Transport licenses', 'Driver permits, payroll and A1', 'VAT refunds on fuel and tolls abroad']
    },
    {
      icon: LifeBuoy,
      title: 'Business rescue',
      desc: 'When times get tough, you need a plan and someone at the table with creditors.',
      features: ['Financial analysis and rescue plans', 'Creditor negotiations', 'Compulsory settlement and restructuring']
    }
  ];

  return (
    <section id="services" className="bg-[#F6F2EA] py-24 lg:py-32" data-testid="services-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-[120px]">
        {/* Header */}
        <div className="max-w-[820px] mb-14">
          <span className="text-[13px] font-bold tracking-[2.5px] text-[#7A5B28] block mb-5">
            {sl ? 'KAJ PREVZAMEMO NAMESTO VAS' : 'WHAT WE TAKE OFF YOUR PLATE'}
          </span>
          <h2 className="font-serif text-[40px] lg:text-[64px] leading-[1.02] tracking-[-1px]">
            {sl ? (
              <>Vse, kar vaše podjetje potrebuje — <span className="italic">pod eno streho.</span></>
            ) : (
              <>Everything your business needs — <span className="italic">under one roof.</span></>
            )}
          </h2>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, serviceIndex) => {
            const Icon = service.icon;
            return (
              <div 
                key={service.title}
                className="bg-[#FFFDF8] border border-[#E2DACB] p-8 lg:p-10 flex flex-col gap-4"
                data-testid={`service-card-${serviceIndex}`}
              >
                <div className="w-14 h-14 bg-[#0E1114] text-[#C9A462] flex items-center justify-center">
                  <Icon className="w-6 h-6" strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-[28px] lg:text-[32px] leading-[1.1]">{service.title}</h3>
                <p className="text-[15px] text-[#4E5359] leading-[1.6]">{service.desc}</p>
                <div className="border-t border-[#E2DACB] pt-4 mt-auto space-y-2">
                  {service.features.map((feature, featureIndex) => (
                    <div key={feature} className="text-[14px] text-[#15181C]">
                      — {featureIndex === service.features.length - 1 ? <strong>{feature}</strong> : feature}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Office Rental Band */}
        <div id="office" className="mt-6 bg-[#E9E2D4] p-6 lg:p-12 flex flex-col lg:flex-row gap-6 lg:gap-12 items-center">
          <div className="flex gap-3 flex-shrink-0">
            <picture>
              <source srcSet="/images/office-rental.webp" type="image/webp" />
              <img 
                src="/images/office-rental.jpg" 
                alt={sl ? 'Svetla prazna pisarna za najem' : 'Bright empty office for rent'}
                className="w-[180px] lg:w-[260px] h-[160px] lg:h-[240px] object-cover"
                loading="lazy"
              />
            </picture>
            <picture>
              <source srcSet="/images/office-private.webp" type="image/webp" />
              <img 
                src="/images/office-private.jpg" 
                alt={sl ? 'Opremljena pisarna' : 'Furnished office'}
                className="w-[120px] lg:w-[180px] h-[160px] lg:h-[240px] object-cover"
                loading="lazy"
              />
            </picture>
          </div>
          <div className="flex-1">
            <span className="text-[13px] font-bold tracking-[2.5px] text-[#7A5B28] block mb-3">
              {sl ? 'NAJEM PISARN · ROGATEC' : 'OFFICE RENTAL · ROGATEC'}
            </span>
            <h3 className="font-serif text-[28px] lg:text-[40px] leading-[1.05] mb-3">
              {sl ? (
                <>Potrebujete tudi pisarno? <span className="italic">Preselite se k svojemu računovodji.</span></>
              ) : (
                <>Need an office too? <span className="italic">Move in with your accountant.</span></>
              )}
            </h3>
            <p className="text-[16px] text-[#4E5359] leading-[1.6]">
              {sl 
                ? 'Opremljene pisarne od 20 do 40 m² na Celjski cesti 47 — klimatizacija, optični internet in brezplačno parkiranje.'
                : 'Furnished offices from 20 to 40 m² at Celjska cesta 47 — air conditioning, fiber internet and free parking.'}
            </p>
          </div>
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="flex-shrink-0 border border-[#15181C] text-[#15181C] font-bold text-[15px] px-6 py-4 no-underline hover:bg-[#15181C] hover:text-[#F6F2EA] transition-colors"
          >
            {sl ? 'Preverite razpoložljivost' : 'Check availability'}
          </a>
        </div>
      </div>
    </section>
  );
};

export default NewServicesSection;
